/**
 * Quotation formats — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_office_formats`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const quotationFormatsPack: ToilePack = {
    slug: 'quotation-formats',
    category: 'office',
    name: 'Quotation formats',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'services', label: 'Services', subject: 'a quotation format with a table of items, rate, quantity and total, and a header "QUOTATION"'},
        {key: 'goods', label: 'Goods', subject: 'a quotation format with GST columns'},
        {key: 'construction', label: 'Construction', subject: 'a quotation format for building work'},
    ],
    styles: [
        {key: 'grid', label: 'Grid', style: 'clean ruled table with thin black borders, bold column headings and a boxed total', palette: 'black ink on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'blue', label: 'Blue header', style: 'modern table with a solid blue header row, light grey zebra rows and a bold total panel', palette: 'blue and light grey on white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
        {key: 'amber', label: 'Amber', style: 'sturdy business form with an amber title bar and heavy table lines', palette: 'amber, charcoal and white', framing: 'single page seen straight on, centered on a plain light grey ground with an even white margin around the sheet'},
    ],
    directions: ['green header', 'dark header', 'with logo'],
    directionHint: 'try: green header',
    seo: {
        title: 'Quotation formats: services, goods, construction',
        metaDescription: 'Nine printable quotation formats for services, goods and construction, each in a grid, blue header or amber table style.',
        h1: 'Quotation format: services, goods, construction',
        tagline: 'Three quotation tables, each in three styles, with items, rates, quantities and a total.',
        intro: 'A quotation format is the table a seller uses to set out work or goods before a sale. These nine show a services quotation with items, rate, quantity and total; a goods quotation with GST columns; and a construction quotation. Only the headings and total are lettered; grey lines hold your figures. Type a twist, such as dark header, and it is a request.',
        useCases: [
            'a quote for a service',
            'a quote for goods with tax',
            'an estimate for a building or repair job',
            'a quote to print and hand to a client',
        ],
        faq: [
            {q: 'Are the figures filled in?', a: 'No. The table shows the column headings and a total box. The figures are grey lines you fill in.'},
            {q: 'What does the GST column show?', a: 'The goods quotation has GST columns, shown as blank columns for the tax on each line. The figures are yours to set.'},
            {q: 'How do the three table styles differ?', a: 'Grid is plain black ink on white. Blue header has a solid blue header row and light grey rows. Amber has an amber title bar and heavy table lines.'},
            {q: 'Can I change the header colour?', a: 'Type it as a twist, such as green header. It is a request for that colour, not a promised result.'},
        ],
    },
}
